import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@heroui/react';
import { apiFetch } from '@/lib/api';
import type { Client, Location } from '@/lib/types';
import { useUserStore } from '@/store/userSlice';

export interface LocationFormValues {
  client_id: string;
  internal_id: string;
  internal_id_2: string;
  name: string;
  type: string;
  city: string;
  region: string;
  province: string;
  district: string;
  address: string;
  phone: string;
  url: string;
  description: string;
  latitude: string;
  longitude: string;
}

const EMPTY_FORM: LocationFormValues = {
  client_id: '',
  internal_id: '',
  internal_id_2: '',
  name: '',
  type: '',
  city: '',
  region: '',
  province: '',
  district: '',
  address: '',
  phone: '',
  url: '',
  description: '',
  latitude: '',
  longitude: '',
};

async function readError(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? `Ошибка запроса (${res.status})`;
}

function parseCoordinateInput(raw: string): number | null {
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

export function useLocationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isStaff = useUserStore((s) => s.isStaff);
  // /locations/new — создание, /locations/:id — редактирование
  const locationId = !id || id === 'new' ? null : id;

  const [location, setLocation] = useState<Location | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<LocationFormValues>(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(Boolean(locationId));
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Выбор клиента — только для персонала; клиенту client_id подставляет API
  useEffect(() => {
    if (!isStaff) return;
    void apiFetch('/api/v1/admin/clients')
      .then((res) => (res.ok ? res.json() : []))
      .then((list: Client[]) => setClients(list))
      .catch(() => setClients([]));
  }, [isStaff]);

  const fetchLocation = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!locationId) return;
      const silent = options?.silent ?? false;
      if (!silent) setIsLoading(true);
      setNotFound(false);
      try {
        const res = await apiFetch(`/api/v1/admin/locations/${locationId}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error(await readError(res));
        const data = (await res.json()) as Location;
        setLocation(data);
        setForm({
          client_id: data.clientId,
          internal_id: data.internalId,
          internal_id_2: data.internalId2 ?? '',
          name: data.name,
          type: data.type ?? '',
          city: data.city ?? '',
          region: data.region ?? '',
          province: data.province ?? '',
          district: data.district ?? '',
          address: data.address ?? '',
          phone: data.phone ?? '',
          url: data.url ?? '',
          description: data.description ?? '',
          latitude: data.latitude ?? '',
          longitude: data.longitude ?? '',
        });
      } catch (e) {
        toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить локацию');
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [locationId],
  );

  useEffect(() => {
    void fetchLocation();
  }, [fetchLocation]);

  const setField = <K extends keyof LocationFormValues>(key: K, value: LocationFormValues[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.internal_id.trim()) {
      toast.danger('Укажите внутренний ID');
      return;
    }
    if (!form.name.trim()) {
      toast.danger('Укажите название локации');
      return;
    }
    if (isStaff && !locationId && !form.client_id) {
      toast.danger('Выберите клиента');
      return;
    }

    const payload: Record<string, unknown> = {
      internal_id: form.internal_id.trim(),
      name: form.name.trim(),
    };
    if (isStaff && form.client_id) payload.client_id = form.client_id;
    for (const key of [
      'internal_id_2',
      'type',
      'city',
      'region',
      'province',
      'district',
      'address',
      'phone',
      'url',
      'description',
    ] as const) {
      const value = form[key].trim();
      payload[key] = value === '' ? null : value;
    }
    payload.latitude = form.latitude.trim() === '' ? null : parseCoordinateInput(form.latitude);
    payload.longitude = form.longitude.trim() === '' ? null : parseCoordinateInput(form.longitude);

    setIsSaving(true);
    try {
      const res = locationId
        ? await apiFetch(`/api/v1/admin/locations/${locationId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await apiFetch('/api/v1/admin/locations', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
      if (!res.ok) throw new Error(await readError(res));

      const saved = (await res.json()) as Location;
      toast.success(locationId ? 'Локация обновлена' : 'Локация создана');
      if (!locationId) {
        navigate(`/locations/${saved.id}`, { replace: true });
      } else {
        await fetchLocation({ silent: true });
      }
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось сохранить локацию');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!location || isToggling) return;
    setIsToggling(true);
    try {
      const res = await apiFetch(
        `/api/v1/admin/locations/${location.id}/${enabled ? 'activate' : 'deactivate'}`,
        { method: 'PATCH' },
      );
      if (!res.ok) throw new Error(await readError(res));
      const updated = (await res.json()) as Location;
      setLocation((prev) =>
        prev ? { ...prev, enabled: updated.enabled, deactivatedAt: updated.deactivatedAt } : prev,
      );
      toast.success(enabled ? 'Локация активирована' : 'Локация деактивирована');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось изменить статус');
    } finally {
      setIsToggling(false);
    }
  };

  return {
    isStaff,
    locationId,
    location,
    clients,
    form,
    isLoading,
    notFound,
    isSaving,
    isToggling,
    setField,
    handleSave,
    handleToggleEnabled,
  };
}
